/**
 * CentralReach integration constants + pure URL classifier.
 *
 * CentralReach is an ABA/therapy practice-management platform. Unlike ModMed
 * and ECW it is SaaS-only today (`members.centralreach.com`), so the host
 * suffix is the primary signal — but we still classify by URL SHAPE as well so
 * a future regional/vanity host is recognized without a code change.
 *
 * Routing is hash-based:
 *   https://members.centralreach.com/#contacts/details/?id=3681455
 *                                     └ module ┘└ view ┘  └ client id ┘
 *
 * Browser-free (no DOM): shared by the extension, web app and gateway.
 */

import type { EmrPageType, IntegrationMetadata } from './types';

export const CENTRALREACH_INTEGRATION_ID = 'centralreach' as const;

/**
 * Host suffixes CentralReach is served from. Matching the SUFFIX (not the full
 * host) keeps regional/tenant subdomains working. Detection FAST-PATH only —
 * the URL-shape test below recognizes the app structurally regardless of host.
 */
export const CENTRALREACH_HOST_SUFFIXES = {
  production: ['.centralreach.com'],
  sandbox: [] as string[],
} as const;

export const centralReachIntegrationMetadata: IntegrationMetadata = {
  id: CENTRALREACH_INTEGRATION_ID,
  name: 'CentralReach',
  description: 'Real-time client context from CentralReach',
  enabled: true,
};

/** Contact-details route, e.g. `#contacts/details/?id=3681455`. */
const CONTACT_DETAILS_HASH = /#\/?contacts\/details\/?\?(?:[^#]*&)?id=(\d+)/i;

/**
 * Structural test for "this is the CentralReach app".
 *
 * NOTE — deliberately host-ANCHORED, unlike ModMed/ECW. Their URL shapes are
 * self-identifying (`/webemr/`, ModMed's practice route), but CentralReach's
 * hash routes (`#contacts`, `#billing`, `#scheduling`) are far too generic to
 * be safe on their own: matching them host-independently would attach the
 * bridge to any unrelated SPA that happens to use a `#contacts` route. So we
 * require BOTH a CentralReach host suffix AND an app hash route. Vanity/
 * regional hosts are still covered — the provider falls back to a DOM
 * signature for those (see `centralReachProvider.detect`).
 */
export function isCentralReachUrlShape(rawUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }
  const hostMatches = CENTRALREACH_HOST_SUFFIXES.production.some(suffix =>
    url.hostname.endsWith(suffix)
  );
  if (!hostMatches) return false;
  return /#\/?(contacts|billing|claims|scheduling|calendar)(\/|\?|$)/i.test(url.hash);
}

/**
 * Pure URL → page-type classifier for CentralReach.
 *
 * The client id IS in the URL (`#contacts/details/?id=<n>`), so unlike ECW we
 * can resolve `emrPatientId` without touching the DOM. Other modules classify
 * as their page type with a null id — the bridge still attaches so it can
 * observe, but exposes no patient identity from the URL alone.
 */
export function classifyCentralReachUrl(rawUrl: string): {
  isSupported: boolean;
  pageType: EmrPageType;
  emrPatientId: string | null;
} {
  if (!isCentralReachUrlShape(rawUrl)) {
    return { isSupported: false, pageType: 'unknown', emrPatientId: null };
  }

  let hash = '';
  try {
    hash = new URL(rawUrl).hash;
  } catch {
    return { isSupported: false, pageType: 'unknown', emrPatientId: null };
  }

  // Client chart — the only route that carries patient identity.
  const details = CONTACT_DETAILS_HASH.exec(hash);
  if (details?.[1]) {
    return { isSupported: true, pageType: 'patient_chart', emrPatientId: details[1] };
  }

  // Claims BEFORE billing: CentralReach's real claims route is `#billing/claims`,
  // which the looser billing test would otherwise swallow.
  if (/claims/i.test(hash)) {
    return { isSupported: true, pageType: 'claims', emrPatientId: null };
  }
  if (/#\/?billing/i.test(hash)) {
    return { isSupported: true, pageType: 'billing', emrPatientId: null };
  }
  if (/#\/?(scheduling|calendar)/i.test(hash)) {
    return { isSupported: true, pageType: 'scheduling', emrPatientId: null };
  }

  // A supported CentralReach route we don't extract from yet (e.g. the
  // contacts LIST) — attach for observability, expose no identity.
  return { isSupported: true, pageType: 'unknown', emrPatientId: null };
}
